import {Inertia} from '@inertiajs/inertia'

const state = () => ({
    automation: {},
    backState: [],
    campaigns: [],
    lists: [],
    creatableObjects: [],
    findableObjects: [],
    undoHistory: [],
    redoHistory: [],
    undoHistoryPosition: -1,
    redoHistoryPosition: -1,
    initialising: true,
    nodePlacement: '',
    objectsWithParents: [],
    overflowing: false,
    personalisableObjects: {},
    readableFields: {},
    saving: false,
    triggerObjects: [],
    unsavedChanges: false,
    updating: false,
    users: [],
    writeableFields: {},
    zoom: 100
})

const getters = {
    childrenNodes: (state, getters) => (node) => {
        return getters.collectChildren(node.id)
    },
    collectChildren: (state, getters) => (nodeId, collectedChildren = []) => {
        const children = getters.nodes.filter(node => node.parentId === nodeId)
        if (children.length === 0) return collectedChildren
        collectedChildren.push(...children)
        return getters.collectChildren(children[0].id, collectedChildren)
    },
    directChildren: (state, getters) => (nodeId) => {
        return getters.nodes.filter(node => node.parentId === nodeId)
    },
    fieldsForObjects: ({readableFields, writeableFields}) => (objects, access = 'readable') => {
        const fields = access === 'readable'
            ? readableFields
            : writeableFields

        if (typeof objects === 'string') {
            return fields[objects]
        }

        const filteredFields = Object.entries(fields)
            .filter(([key]) => {
                return objects.find(object => key === object)
            })

        return Object.fromEntries(filteredFields)
    },
    fieldsForPersonalisableObjects: (state, getters) => (objects, access = 'readable') => {
        const fields = getters.fieldsForObjects(objects, access)

        const filteredFields = Object.entries(fields).filter(([key]) => {
            return state.personalisableObjects.find(object => key === object.Name)
        })

        return Object.fromEntries(filteredFields)
    },
    hasCampaigns: ({campaigns}) => {
        return campaigns.length > 0
    },
    hasLists: ({lists}) => {
        return lists.length > 0
    },
    hasChildren: (state, getters) => (nodeId) => {
        return getters.nodes.some(node => node.parentId === nodeId)
    },
    latestBackState: ({backState}) => {
        return backState[backState.length - 1]
    },
    nextNodeId: (state, getters) => {
        return getters.nodes
            .map(node => node.id)
            .reduce((previousValue, currentValue) => previousValue > currentValue ? previousValue : currentValue) + 1
    },
    nodeExists: ({automation}, getters) => (node) => {
        return automation.hasOwnProperty('json_definition')
            ? getters.nodes.find(n => n.id === node.id) !== undefined
            : false
    },
    nodes: ({automation}) => {
        return automation.hasOwnProperty('json_definition')
            ? automation.json_definition.map((row) => {
                return row.nodes
            }).flat()
            : []
    },
    parentNode: ({automation}, getters) => (parentId) => {
        return getters.nodes.find(node => node.id === parentId)
    },
    rowExists: ({automation}) => (row) => {
        return automation.json_definition[row] !== undefined
    },
    rows: ({automation}) => {
        return automation.json_definition
    },
    shouldPruneForTrigger: (state, getters) => (node) => {
        return getters.triggerExists
            ? getters.trigger.object[0] !== node.object[0]
            : false
    },
    trigger: ({automation}, getters) => {
        return getters.triggerExists
            ? automation.json_definition[0].nodes[0]
            : {}
    },
    triggerExists: ({automation}) => {
        return automation.hasOwnProperty('json_definition')
            ? automation.json_definition[0].nodes.length > 0
            : false
    },
    validCreatableObjects: ({creatableObjects}, getters) => {
        return getters.triggerExists
            ? creatableObjects.filter(object => !getters.trigger.object?.includes(object.Name))
            : creatableObjects
    },
    validObjectNames: (state, getters) => (parentId) => {
        const validObjects = getters.validObjects(parentId)
        return validObjects.map(object => object.Name)
    },
    validObjects: ({objectsWithParents}, getters) => (parentId) => {
        if (parentId === 0) return
        const parentNode = getters.parentNode(parentId)
        // flatten objects down into single level array
        const objects = parentNode.availableObjects.flatMap(object => objectsWithParents[object])
        // remove duplicates before returning
        return [...new Map(objects.map(object => [object.Name, object])).values()]
    }
}

const mutations = {
    addNode({automation}, {row, node}) {
        automation.json_definition[row].nodes.push(node)
    },
    addRow({automation}) {
        automation.json_definition.push({
            nodes: []
        })
    },
    insertRow({automation}, {position}) {
        automation.json_definition.splice(position, 0, {
            nodes: []
        })
    },
    moveUndoHistoryPosition(state, move = 0) {
        const lastUndoPosition = state.undoHistory.length - 1
        if (state.undoHistoryPosition === lastUndoPosition && move === 1) return
        if (state.undoHistoryPosition === -1 && move === -1) return
        if (move !== 0) {
            return state.undoHistoryPosition += move
        }
        state.undoHistoryPosition = lastUndoPosition
    },
    moveRedoHistoryPosition(state, move = 0) {
        const lastRedoPosition = state.redoHistory.length - 1
        if (state.redoHistoryPosition === -1 && move === -1) return
        if (state.redoHistoryPosition === lastRedoPosition && move === 1) return
        if (move !== 0) {
            return state.redoHistoryPosition += move
        }
        state.redoHistoryPosition = lastRedoPosition
    },
    popBackState(state) {
        state.backState.pop()
    },
    pushBackState(state, backState) {
        state.backState.push(backState)
    },
    removeAutomationTrigger({automation}) {
        automation.json_definition = [
            {
                nodes: []
            }
        ]
    },
    removeNode({automation}, {row, nodeId}) {
        const nodeIndex = automation.json_definition[row].nodes.findIndex(node => node.id === nodeId)
        automation.json_definition[row].nodes.splice(nodeIndex, 1)
    },
    removeRow({automation}, {position}) {
        automation.json_definition.splice(position, 1)
    },
    resetBackState(state) {
        state.backState = []
    },
    resetAutomationStore(state) {
        state.backState = []
        state.initialising = true
        state.nodePlacement = ''
        state.overflowing = false
        state.saving = false
        state.unsavedChanges = false
        state.updating = false
        state.zoom = 100
    },
    setAutomation(state, {automation}) {
        state.automation = automation
    },
    setAutomationJson(state, undo = true) {
        state.automation.json_definition = undo
            ? state.undoHistory[state.undoHistoryPosition]
            : state.redoHistory[state.redoHistoryPosition]
    },
    setAutomationLive(state, {isLive}) {
        state.automation.live = isLive
    },
    setAutomationName(state, {name}) {
        state.automation.name = name
    },
    setFolder(state, {folder}) {
        state.automation.folder_id = folder
    },
    setCampaigns(state, {campaigns}) {
        state.campaigns = campaigns
    },
    setLists(state, {lists}) {
        state.lists = lists
    },
    setCreatableObjects(state, {creatableObjects}) {
        state.creatableObjects = creatableObjects
    },
    setFindableObjects(state, {objects}) {
        state.findableObjects = objects
    },
    setInitialising(state, {initialising}) {
        state.initialising = initialising
    },
    setNodePlacement(state, {placement}) {
        state.nodePlacement = placement
    },
    setObjectReadableFields(state, {object, fields}) {
        state.readableFields[object] = fields
    },
    setObjectsWithParents(state, {objects}) {
        state.objectsWithParents = objects
    },
    setObjectWriteableFields(state, {object, fields}) {
        state.writeableFields[object] = fields
    },
    setOverflow(state) {
        const multiplier = state.zoom / 100
        const nodes = document.getElementById('nodes')
        const container = document.getElementById('container')
        state.overflowing = (nodes.offsetWidth * multiplier) >= container.offsetWidth
    },
    setPersonalisableObjects(state, {objects}) {
        state.personalisableObjects = objects
    },
    setReadableFields(state, {fields}) {
        state.readableFields = fields
    },
    setRedoHistory({redoHistory, automation}) {
        redoHistory.splice(9, 1, _.cloneDeep(automation.json_definition))
    },
    setSaving(state, saving) {
        state.saving = saving
    },
    setTriggerObjects(state, {triggerObjects}) {
        state.triggerObjects = triggerObjects
    },
    setUndoHistory({undoHistory, automation}) {
        undoHistory.splice(9, 1, _.cloneDeep(automation.json_definition))
    },
    setUnsavedChanges(state, unsaved) {
        state.unsavedChanges = unsaved
    },
    setUpdating(state, updating) {
        state.updating = updating
    },
    setUsers(state, {users}) {
        state.users = users
    },
    setWriteableFields(state, {fields}) {
        state.writeableFields = fields
    },
    setZoom(state, zoom) {
        state.zoom = zoom
    },
    updateNode({automation}, {row, node}) {
        const nodeIndex = automation.json_definition[row].nodes.findIndex(currentNode => currentNode.id === node.id)
        automation.json_definition[row].nodes.splice(nodeIndex, 1, node)
    }
}

const actions = {
    async addOrInsertRow({commit, state, getters}, {row, node, withinBranch}) {
        commit('setUndoHistory')
        commit('moveUndoHistoryPosition')

        if (getters.rowExists(row) && state.nodePlacement === 'branch' || getters.nodeExists(node)) return
        if (getters.rowExists(row) && state.nodePlacement === 'insert' && withinBranch) return
        if (getters.rowExists(row) && state.nodePlacement === 'insert' && !withinBranch) {
            return commit('insertRow', {
                position: row
            })
        }
        commit('addRow')
    },
    async addOrUpdateNode({commit, dispatch, state, getters}, {row, node, withinBranch}) {
        commit('setUnsavedChanges', true)
        commit('setUpdating', true)

        await dispatch('pruneNodes', {
            row,
            node,
            placement: state.nodePlacement,
            withinBranch
        })
        if (getters.nodeExists(node)) {
            commit('updateNode', {
                row,
                node
            })
            return commit('setUpdating', false)
        }
        commit('addNode', {
            row,
            node
        })
        await dispatch('moveChildren', {
            row,
            node,
            withinBranch
        })
        commit('setUpdating', false)
        dispatch('assessIfBranched', {
            row,
            node
        })
    },
    async addOrUpdateTrigger({commit, dispatch, state, getters}, {row, node}) {
        commit('setUndoHistory')
        commit('moveUndoHistoryPosition')
        commit('setUnsavedChanges', true)

        if (getters.shouldPruneForTrigger(node)) {
            await dispatch('pruneNodes', {
                row,
                node,
                placement: null,
                withinBranch: false
            })
        }
        if (getters.triggerExists) {
            return commit('updateNode', {
                row,
                node
            })
        }
        commit('addNode', {
            row,
            node
        })
    },
    async assessIfBranched({dispatch, state, getters}, {row, node}) {
        if (state.nodePlacement === 'branch') {
            return dispatch('updateBranchedState', {
                row: row - 1,
                nodeId: node.parentId,
                branched: true
            })
        }
        if (getters.directChildren(node.id).length > 1) {
            dispatch('updateBranchedState', {
                row: row,
                nodeId: node.id,
                branched: true
            })
        }
        dispatch('updateBranchedState', {
            row: row - 1,
            nodeId: node.parentId,
            branched: false
        })
    },
    async deleteNode({commit, dispatch, getters}, {row, node, withinBranch, nodeOnly = false}) {
        const nodes = getters.directChildren(node.parentId)
        const childNodes = getters.directChildren(node.id)

        if (nodes.length === 2 && !nodeOnly) {
            await dispatch('updateBranchedState', {
                row: row - 1,
                nodeId: node.parentId,
                branched: false
            })
        }

        if (nodeOnly) {
            await dispatch('updateBranchedState', {
                row: row - 1,
                nodeId: node.parentId,
                branched: childNodes.length
                    + nodes.length > 2
            })
            await dispatch('moveChildren', {
                row: row + 1,
                node: node,
                withinBranch,
                direction: 'up'
            })
        }

        commit('removeNode', {
            row,
            nodeId: node.id
        })

        getters.rows.forEach((row, position) => {
            if (row.nodes.length === 0) {
                commit('removeRow', {
                    position: position
                })
            }
        })
    },
    async deleteNodeWithDependents({commit, dispatch, getters}, {row, node, withinBranch}) {
        commit('setUnsavedChanges', true)
        commit('setUndoHistory')
        commit('moveUndoHistoryPosition')
        commit('setUpdating', true)

        const objects = getters.validObjectNames(node.parentId)
        // if the new object is valid, only delete the node itself and update dependents parents
        if (objects.filter(object => node.availableObjects.includes(object)).length) {
            await dispatch('deleteNode', {
                row,
                node,
                nodeOnly: true,
                withinBranch
            })
            getters.directChildren(node.id).forEach(child => {
                child.parentId = node.parentId
                commit('updateNode', {
                    row,
                    node: child
                })
            })
            return commit('setUpdating', false)
        }
        await dispatch('removeDependents', {
            row,
            nodeId: node.id
        })
        await dispatch('deleteNode', {
            row,
            node,
            withinBranch
        })
        commit('setUpdating', false)
    },
    async forwardToHistoryEntry({commit}) {
        commit('setUpdating', true)
        commit('moveUndoHistoryPosition', 1)
        commit('setAutomationJson', false)
        commit('moveRedoHistoryPosition', -1)
        commit('setUpdating', false)
    },
    async loadFindableObjects({commit, state}) {
        if (state.findableObjects.length > 0) return

        const objects = await axios.get('/ajax/automation/findable/objects')
        commit('setFindableObjects', {
            objects: objects.data
        })
    },
    async loadReadableFieldsForObject({commit, state}, object) {
        if (state.readableFields.hasOwnProperty(object)) return

        const fields = await axios.get(route('ajax.object.fields', {
            object_detail: object,
            access: 'readable'
        }))
        commit('setObjectReadableFields', {
            object,
            fields: fields.data
        })
    },
    async loadWriteableFieldsForObject({commit, state}, object) {
        if (state.writeableFields.hasOwnProperty(object)) return

        const fields = await axios.get(route('ajax.object.fields', {
            object_detail: object,
            access: 'writeable'
        }))
        commit('setObjectWriteableFields', {
            object,
            fields: fields.data
        })
    },
    async moveChildren({commit, dispatch, getters}, {row, node, withinBranch, direction = 'down'}) {
        const addToRow = direction === 'down' ? 1 : -1
        if (!getters.hasChildren(node.id) || !withinBranch) return
        if (!getters.rowExists(row + addToRow)) {
            commit('addRow')
        }
        // recursively add child nodes to new row and then remove from old row
        getters.directChildren(node.id).forEach(child => {
            commit('addNode', {
                row: row + addToRow,
                node: child
            })

            commit('removeNode', {
                row: row,
                nodeId: child.id
            })

            return dispatch('moveChildren', {
                row: row + 1,
                node: child,
                withinBranch,
                direction
            })
        })
    },
    async pruneNodes({commit, dispatch, getters}, {row, node, placement, withinBranch}) {
        const nodeExists = getters.nodeExists(node)
        const triggerOrFind = node.type === 'trigger' || node.type === 'find'
        const parentHasChildren = getters.hasChildren(node.parentId)
        const children = getters.directChildren(node.parentId)

        if (nodeExists && !triggerOrFind) return
        if (!nodeExists && placement === 'branch') return
        if (!nodeExists && parentHasChildren) {
            if (triggerOrFind) {
                return dispatch('removeDependents', {
                    row: row - 1,
                    nodeId: node.parentId,
                    childRowAdd: placement === 'insert' && !withinBranch
                        ? 2
                        : 1
                })
            }

            return children.forEach(child => {
                child.parentId = node.id
                commit('updateNode', {
                    row: !withinBranch ? row + 1 : row,
                    node: child
                })
            })
        }

        dispatch('removeDependents', {
            row: row,
            nodeId: node.id
        })
    },
    async removeDependents({commit, dispatch, getters}, {row, nodeId, childRowAdd = 1}) {
        if (!getters.hasChildren(nodeId)) return
        await dispatch('updateBranchedState', {
            row,
            nodeId,
            branched: false
        })
        const children = getters.directChildren(nodeId)
        const childRow = row + childRowAdd

        return children.forEach(child => {
            dispatch('removeDependents', {
                row: childRow,
                nodeId: child.id
            }).then(() => {
                commit('removeNode', {
                    row: childRow,
                    nodeId: child.id
                })
                if (getters.rows[childRow].nodes.length === 0) {
                    commit('removeRow', {
                        position: childRow
                    })
                }
            })
        })
    },
    async revertToHistoryEntry({commit}) {
        commit('setUpdating', true)
        commit('setRedoHistory')
        commit('setAutomationJson')
        commit('moveUndoHistoryPosition', -1)
        commit('moveRedoHistoryPosition')
        commit('setUpdating', false)
    },
    async saveAutomation({commit, state}) {
        Inertia.patch(`/automations/${state.automation.id}`, {
            folder_id: state.automation.folder_id,
            name: state.automation.name,
            live: state.automation.live,
            json_definition: state.automation.json_definition
        }, {
            onSuccess: () => {
                commit('setSaving', false)
                commit('setUnsavedChanges', false)
            },
            onError: () => {
                commit('setSaving', false)
                commit('setAutomationLive', {isLive: false})
            }
        })
    },
    async updateBranchedState({commit, dispatch, state, getters}, {row, nodeId, branched}) {
        const node = getters.nodes.find(node => node.id === nodeId)
        node.branchedFrom = branched
        commit('updateNode', {
            row,
            node: node
        })
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
